@if (isOpen) {
  <div class="modal-overlay" data-testid="upload-modal">
    <div class="modal-container">
      <!-- Uploading overlay -->
      @if (isUploading) {
        <div class="uploading-overlay">
          <div class="uploading-content">
            <app-spinner [display]="true" [diameter]="48" aria-label="Uploading"></app-spinner>
            <span class="uploading-text">Uploading document…</span>
          </div>
        </div>
      }
      <div class="modal-header">
        <h2 class="modal-title">
          {{ showAffectedEntities() ? 'Affected Entities' : 'Document Upload' }}
        </h2>
        <button
          class="close-button"
          data-testid="upload-modal-close-btn"
          (click)="onCancel()"
          aria-label="Close modal"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
      @if (
        currentStep() === 3 && !showAffectedEntities() && hasSelectedContactMissingRequiredInfo
      ) {
        <app-az-warning-banner testId="bcr-173-missing-name-warning">
          {{ incompleteContactsCountLabel }}
          {{ incompleteSelectedContacts.length === 1 ? 'legal entity has' : 'legal entities have' }}
          incomplete contact details. Review the
          <button
            type="button"
            class="modal-warning__link"
            data-testid="bcr-173-affected-entities-link"
            (click)="showAffectedEntitiesPanel()"
          >
            affected entities
          </button>
          before proceeding.
        </app-az-warning-banner>
      }
      @if (
        currentStep() === 3 && !showAffectedEntities() && hasSelectedContactsExceedingBatchLimit
      ) {
        <app-az-warning-banner variant="info" testId="bcr-253-contacts-batch-limit-info">
          The number of selected signatories exceeds {{ maxContactsPerBatch }}. The submission will
          be automatically split into groups of up to {{ maxContactsPerBatch }} for processing.
        </app-az-warning-banner>
      }
      <!-- Step 1: Replacement file upload -->
      @if (currentStep() === 1) {
        <div
          class="modal-content overlay-scroll--custom"
          scrollActivity
          [scrollActivityCustom]="true"
        >
          <div class="modal-content-inner">
            <p class="modal-description">
              Please upload the <span class="modal-description__emphasis">file</span> for the
              necessary document update.
            </p>
            <div class="upload-wrapper">
              @if (!selectedFile || errorMessage) {
                <div
                  class="upload-zone"
                  data-testid="upload-drop-zone"
                  [class.upload-zone--dragging]="isDragging"
                  [class.upload-zone--error]="errorMessage"
                  (dragover)="onDragOver($event)"
                  (dragleave)="onDragLeave($event)"
                  (drop)="onDrop($event)"
                  (click)="fileInput.click()"
                >
                  <span class="material-symbols-outlined upload-icon">upload_file</span>
                  <p class="upload-text">
                    Drag and drop file here or
                    <label class="file-label" (click)="$event.stopPropagation()">
                      <span class="choose-file-text">Choose File</span>
                      <input
                        #fileInput
                        tabindex="0"
                        type="file"
                        class="file-input"
                        data-testid="upload-file-input"
                        [accept]="acceptedFormats"
                        (change)="onFileSelected($event)"
                      />
                    </label>
                  </p>
                </div>
              }
              @if (errorMessage) {
                <div class="error-message" data-testid="upload-error-message">
                  <span class="material-symbols-outlined">error</span>
                  <span>{{ errorMessage }}</span>
                </div>
              }
              @if (selectedFile && !errorMessage) {
                <div class="selected-file" data-testid="upload-selected-file">
                  <div class="file-info">
                    <span class="material-symbols-outlined file-icon">description</span>
                    <div class="file-details">
                      <span class="file-name">{{ selectedFileName }}</span>
                      <span class="file-size">{{ selectedFileSize }}</span>
                    </div>
                  </div>
                  <button
                    class="remove-file-btn"
                    data-testid="upload-remove-file-btn"
                    (click)="removeFile()"
                    type="button"
                  >
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              }
              <div class="upload-info">
                <span class="upload-info-text">Supported formats: {{ supportedFormatsText }}</span>
                <span class="upload-info-text">Maximum size: 10MB</span>
              </div>
            </div>
            @if (shouldShowDocumentTypeSelector) {
              <div
                class="document-type-fields"
                [class.document-type-fields--custom-enabled]="supportsCustomDocumentName"
                data-testid="upload-file-type-section"
              >
                <div class="form-group">
                  <app-az-selector
                    data-testid="upload-file-type-dropdown"
                    [label]="'Uploaded File'"
                    [options]="documentTypes"
                    [selectedValue]="selectedDocumentType"
                    [selectedOptionKey]="selectedDocumentOptionKey"
                    [disabled]="disableDocumentTypeSelector"
                    (selectionChange)="onDocumentTypeChange($event)"
                    (optionSelected)="onDocumentTypeOptionChange($event)"
                  ></app-az-selector>
                </div>
                @if (supportsCustomDocumentName && isCustomDocumentSelected) {
                  <div class="form-group document-name-field">
                    <label class="form-label" for="custom-document-name">File Name</label>
                    <input
                      id="custom-document-name"
                      class="form-input"
                      data-testid="custom-document-name"
                      type="text"
                      autocomplete="off"
                      [(ngModel)]="newDocumentName"
                      [attr.aria-invalid]="!isNewDocumentNameValid"
                      aria-describedby="custom-document-name-warning"
                    />
                    @if (!isNewDocumentNameValid) {
                      <span
                        class="form-warning"
                        id="custom-document-name-warning"
                        data-testid="custom-document-name-warning"
                        role="alert"
                      >
                        {{ newDocumentNameWarning }}
                      </span>
                    }
                  </div>
                }
              </div>
            }
            @if (showCommentBox) {
              <div class="form-group">
                <label class="form-label" for="upload-comments-textarea">Comments:</label>
                <textarea
                  id="upload-comments-textarea"
                  class="form-textarea"
                  data-testid="upload-comments-textarea"
                  [(ngModel)]="comments"
                  rows="4"
                ></textarea>
              </div>
            }
            <div class="modal-actions">
              <app-az-button
                data-testid="upload-cancel-btn"
                [label]="'Cancel Upload'"
                [showIcon]="false"
                (click)="onCancel()"
                variant="default"
                [disabled]="isUploading"
              ></app-az-button>
              <app-az-button
                data-testid="upload-confirm-btn"
                [label]="'Confirm Upload'"
                [showIcon]="false"
                (click)="onConfirm()"
                variant="primary"
                [disabled]="confirmUploadDisabled"
              ></app-az-button>
            </div>
          </div>
        </div>
      }
      <!-- Step 2: Adobe signing contract upload -->
      @if (currentStep() === 2) {
        <div
          class="modal-content overlay-scroll--custom"
          scrollActivity
          [scrollActivityCustom]="true"
          data-testid="bcr-173-notification-step"
        >
          <div class="modal-content-inner">
            <p class="modal-description">
              Please provide the <span class="modal-description__emphasis">contract</span> that will
              be sent out and signed to validate the document change.
            </p>
            <!-- Adobe PDF upload zone -->
            <div class="upload-wrapper">
              @if (!adobePdfFile() || adobePdfError()) {
                <div
                  class="upload-zone"
                  data-testid="bcr-173-adobe-pdf-drop-zone"
                  [class.upload-zone--dragging]="isDragging"
                  [class.upload-zone--error]="adobePdfError()"
                  (dragover)="onAdobePdfDragOver($event)"
                  (dragleave)="onAdobePdfDragLeave($event)"
                  (drop)="onAdobePdfDrop($event)"
                  (click)="adobePdfInput.click()"
                >
                  <span class="material-symbols-outlined upload-icon">upload_file</span>
                  <p class="upload-text">
                    Drag and drop file here or
                    <label class="file-label" (click)="$event.stopPropagation()">
                      <span class="choose-file-text">Choose File</span>
                      <input
                        #adobePdfInput
                        tabindex="0"
                        type="file"
                        class="file-input"
                        data-testid="bcr-173-adobe-pdf-input"
                        accept=".pdf"
                        (change)="onAdobePdfSelected($event)"
                      />
                    </label>
                  </p>
                </div>
              }
              @if (adobePdfError()) {
                <div class="error-message" data-testid="bcr-173-adobe-pdf-error">
                  <span class="material-symbols-outlined">error</span>
                  <span>{{ adobePdfError() }}</span>
                </div>
              }
              @if (adobePdfFile() && !adobePdfError()) {
                <div class="selected-file" data-testid="bcr-173-adobe-pdf-selected-file">
                  <div class="file-info">
                    <span class="material-symbols-outlined file-icon">description</span>
                    <div class="file-details">
                      <span class="file-name">{{ adobePdfFile()?.name }}</span>
                      <span class="file-size">{{ adobePdfFileSize }}</span>
                    </div>
                  </div>
                  <button
                    class="remove-file-btn"
                    data-testid="bcr-173-adobe-pdf-remove-btn"
                    (click)="removeAdobePdf()"
                    type="button"
                  >
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              }
              <div class="upload-info">
                <span class="upload-info-text">Supported format: .pdf</span>
                <span class="upload-info-text">Maximum size: 10MB</span>
              </div>
            </div>
            <div class="modal-actions">
              <app-az-button
                data-testid="bcr-173-back-btn"
                [label]="'Back'"
                [showIcon]="false"
                (click)="onBack()"
                variant="default"
                [disabled]="isUploading"
              ></app-az-button>
              <app-az-button
                data-testid="bcr-173-confirm-btn"
                [label]="'Confirm'"
                [showIcon]="false"
                (click)="onNotifyConfirm()"
                variant="primary"
                [disabled]="confirmContractDisabled"
              ></app-az-button>
            </div>
          </div>
        </div>
      }
      <!-- Step 3: flat recipient selection -->
      @if (currentStep() === 3) {
        <div
          class="modal-content overlay-scroll--custom"
          scrollActivity
          [scrollActivityCustom]="true"
          data-testid="bcr-173-recipient-selection-step"
        >
          <!-- Affected Entities detail panel -->
          @if (showAffectedEntities()) {
            <div class="affected-entities-panel" data-testid="bcr-173-affected-entities-panel">
              <button
                type="button"
                class="affected-entities-back-link"
                data-testid="bcr-173-affected-entities-back-btn"
                (click)="hideAffectedEntitiesPanel()"
              >
                <span class="material-symbols-outlined">chevron_left</span>
                Back to Document Upload
              </button>
              <p class="affected-entities-description">
                The following legal entities are missing important contact information:
              </p>
              <ul class="affected-entities-list">
                @for (contact of incompleteSelectedContacts; track contact) {
                  <li>
                    {{ getLegalEntityDisplayName(contact) }}
                  </li>
                }
              </ul>
            </div>
          }
          @if (!showAffectedEntities()) {
            <div class="modal-content-inner">
              <p class="modal-description">
                Please select the contacts you would like to request a signature from.
              </p>
              <app-az-searchbar
                data-testid="bcr-173-contact-search"
                [search]="true"
                [value]="contactSearchTerm()"
                [placeholder]="'Search'"
                (valueChange)="onContactSearchChanged($event)"
              />
              <div
                class="recipient-table-wrapper overlay-scroll--custom"
                scrollActivity
                [scrollActivityCustom]="true"
                data-testid="bcr-173-contacts-table"
              >
                <table class="recipient-table" [hidden]="isLoadingContacts || contactsError">
                  <thead>
                    <tr>
                      <th class="recipient-table__checkbox">
                        <input
                          type="checkbox"
                          data-testid="bcr-173-select-all-contacts"
                          [checked]="areAllVisibleContactsSelected"
                          (change)="toggleAllVisibleContacts()"
                          aria-label="Select all visible contacts"
                        />
                      </th>
                      <th>Contact Name</th>
                      <th>Email</th>
                      <th>Legal Entity Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (contact of filteredContacts; track contact) {
                      <tr>
                        <td class="recipient-table__checkbox">
                          <input
                            type="checkbox"
                            [attr.data-testid]="'bcr-173-contact-checkbox-' + contact.key"
                            [checked]="selectedContactKeys().has(contact.key)"
                            (change)="toggleContact(contact)"
                            [attr.aria-label]="
                              'Select ' + (contact.contact_name || contact.email || 'contact')
                            "
                          />
                        </td>
                        <td>{{ contact.contact_name || '--' }}</td>
                        <td>{{ contact.email || '--' }}</td>
                        <td>{{ getLegalEntityDisplayName(contact) }}</td>
                      </tr>
                    }
                    @if (filteredContacts.length === 0) {
                      <tr>
                        <td colspan="4" class="recipient-table__empty">No contacts found.</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <div
                  class="recipient-table__state"
                  [hidden]="!isLoadingContacts"
                  data-testid="bcr-173-contacts-loading"
                >
                  <app-spinner [display]="true" [diameter]="32" aria-label="Loading contacts" />
                  Loading contacts…
                </div>
                <div
                  class="recipient-table__state recipient-table__state--error"
                  [hidden]="!contactsError"
                  data-testid="bcr-173-contacts-error"
                >
                  <span>{{ contactsError }}</span>
                  <button
                    type="button"
                    data-testid="bcr-173-retry-contacts"
                    (click)="contactsRequested.emit()"
                  >
                    Retry
                  </button>
                </div>
              </div>
              <div class="modal-actions">
                <strong
                  class="recipient-selection-count"
                  data-testid="bcr-173-selected-contacts-count"
                >
                  {{ legalEntitiesSelectedLabel }} and {{ uniqueContactsCountLabel }}
                </strong>
                <app-az-button
                  data-testid="bcr-173-contact-selection-back-btn"
                  [label]="'Back'"
                  [showIcon]="false"
                  (click)="onStep3Back()"
                  variant="default"
                  [disabled]="isUploading"
                ></app-az-button>
                <app-az-button
                  data-testid="bcr-173-contact-selection-confirm-btn"
                  [label]="'Confirm'"
                  [showIcon]="false"
                  (click)="onContactsConfirm()"
                  variant="primary"
                  [disabled]="
                    isUploading ||
                    isLoadingContacts ||
                    !!contactsError ||
                    selectedContacts.length === 0 ||
                    hasSelectedContactMissingRequiredInfo
                  "
                ></app-az-button>
              </div>
            </div>
          }
        </div>
      }
      <!-- Step 4: Confirmation review -->
      @if (currentStep() === 4) {
        <div
          class="modal-content overlay-scroll--custom"
          scrollActivity
          [scrollActivityCustom]="true"
          data-testid="bcr-173-confirmation-step"
        >
          <div class="modal-content-inner">
            <p class="modal-description">
              You will be sending out the following documents to
              @if (!hasSelectedContactsExceedingBatchLimit) {
                <span class="modal-description__emphasis"
                  >{{ uniqueSelectedContactsCount }} Contacts</span
                >
              } @else {
                <span class="modal-description__emphasis">{{ uniqueContactsCountLabel }}</span
                >. The contract will be processed in
                <span class="modal-description__emphasis">{{ selectedGroupsCountLabel }}</span
                >. A consolidated file will be generated per group upon completion.
              }
            </p>
            <ul class="bcr-173-file-list">
              <li class="bcr-173-file-item" data-testid="bcr-173-confirm-replacement-file">
                <button
                  type="button"
                  class="bcr-173-file-download-btn"
                  (click)="downloadFile($safeNavigationMigration(step1Data()?.file))"
                >
                  <span class="file-name">{{ step1Data()?.file?.name }}</span>
                  <span class="material-symbols-outlined file-icon">description</span>
                </button>
              </li>
              <li class="bcr-173-file-item" data-testid="bcr-173-confirm-adobe-pdf">
                <button
                  type="button"
                  class="bcr-173-file-download-btn"
                  (click)="downloadFile(adobePdfFile())"
                >
                  <span class="file-name">{{ adobePdfFile()?.name }}</span>
                  <span class="material-symbols-outlined file-icon">description</span>
                </button>
              </li>
            </ul>
            <p class="modal-description">
              Are you sure you would like to upload and send these documents?
            </p>
            <div class="modal-actions">
              <app-az-button
                data-testid="bcr-173-step4-back-btn"
                [label]="'Back'"
                [showIcon]="false"
                (click)="onStep4Back()"
                variant="default"
                [disabled]="isUploading"
              ></app-az-button>
              <app-az-button
                data-testid="bcr-173-step4-confirm-btn"
                [label]="'Yes'"
                [showIcon]="false"
                (click)="onStep3Confirm()"
                variant="primary"
                [disabled]="isUploading"
              ></app-az-button>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}
